import { useState, useEffect, Fragment } from "react";
import { DollarSign, Users, AlertTriangle, Zap, TrendingUp, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { type DashboardViewMode } from "@/types/dashboardViews";
import { MarketIntelligenceDialog } from "./MarketIntelligenceDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { CarrierMatchesPane } from "./CarrierMatchesPane";
import { LaneVolumePane } from "./LaneVolumePane";
import { PostDuplicatesPane } from "./PostDuplicatesPane";
import { MarketIntelligencePane } from "./MarketIntelligencePane";
import { PlaceholderPane } from "./PlaceholderPane";
import { VIEW_MODE_LABELS } from "@/types/dashboardViews";

interface AIActionCardProps {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  description: string;
  actionLabel: string;
  onAction?: () => void;
  isLoading?: boolean;
  isActive?: boolean;
}

function AIActionCard({ icon, iconBg, title, description, actionLabel, onAction, isLoading, isActive }: AIActionCardProps) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-card px-3.5 py-2 space-y-1.5 hover:shadow-sm transition-all flex-1 flex flex-col justify-between min-w-0 overflow-hidden",
        isActive && "border-accent ring-1 ring-accent/40 shadow-md"
      )}
      style={{ boxShadow: "var(--shadow-sm)" }}
    >
      <div className="flex items-center gap-2">
        <div className={cn("flex h-6 w-6 items-center justify-center rounded-md flex-shrink-0", iconBg)}>
          {icon}
        </div>
        <p className="text-[12px] font-bold text-foreground truncate">{title}</p>
      </div>
      <p className="text-[11px] text-muted-foreground leading-snug">
        {isLoading ? <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Analyzing…</span> : description}
      </p>
      <button
        onClick={onAction}
        className={cn(
          "w-full flex items-center justify-between rounded-md border px-3 py-1.5 text-[11px] font-semibold transition-colors duration-150",
          isActive
            ? "border-accent bg-accent/10 text-accent"
            : "border-border bg-muted/40 text-foreground hover:bg-muted"
        )}
      >
        <span className="truncate">{isActive ? "Close" : actionLabel}</span>
        <span className={cn(isActive ? "text-accent" : "text-muted-foreground")}>{isActive ? "▾" : "›"}</span>
      </button>
    </div>
  );
}

function InlineExpansion({ view, onClose }: { view: DashboardViewMode; onClose: () => void }) {
  const render = () => {
    switch (view) {
      case "carrier-matches": return <CarrierMatchesPane onBack={onClose} />;
      case "lane-volume": return <LaneVolumePane onBack={onClose} />;
      case "post-duplicates": return <PostDuplicatesPane onBack={onClose} />;
      case "market-intelligence": return <MarketIntelligencePane onBack={onClose} />;
      default: return <PlaceholderPane label={VIEW_MODE_LABELS[view]} onBack={onClose} />;
    }
  };
  return (
    <div className="relative w-full max-w-full min-w-0 rounded-lg border border-accent/40 bg-muted/20 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200">
      {/* Connector arrow */}
      <div className="absolute -top-1.5 left-6 h-3 w-3 rotate-45 border-l border-t border-accent/40 bg-muted/20" />
      <button
        onClick={onClose}
        className="absolute right-2 top-2 z-10 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-label="Close"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <div className="max-h-[420px] overflow-y-auto overflow-x-hidden">
        <div className="min-w-0 w-full max-w-full [&_*]:max-w-full [&_table]:!w-full [&_table]:!table-fixed [&_td]:break-words [&_th]:break-words [&_pre]:whitespace-pre-wrap [&_pre]:break-words">
          {render()}
        </div>
      </div>
    </div>
  );
}

export function AIQuickActionsPanel({ onViewChange, mode = "broker", onMarketIntelligence, onPatternDetector, onTruckExpiration, onLoadMatching, onLaneVolume }: { onViewChange?: (mode: DashboardViewMode) => void; mode?: "broker" | "carrier"; onMarketIntelligence?: () => void; onPatternDetector?: () => void; onTruckExpiration?: () => void; onLoadMatching?: () => void; onLaneVolume?: () => void }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [marketOpen, setMarketOpen] = useState(false);
  const [expandedView, setExpandedView] = useState<DashboardViewMode | null>(null);
  const [stats, setStats] = useState({
    lowPricedLoads: 0,
    matchingCarriers: 0,
    riskAlerts: 0,
    underperformingLanes: 0,
    trendingLanes: 0,
    expiringTrucks: 0,
    postedTrucks: 0,
    isLoading: true,
  });

  useEffect(() => {
    if (!user) { setStats(s => ({ ...s, isLoading: false })); return; }

    const fetchStats = async () => {
      try {
        const now = new Date();
        const today = now.toISOString().split('T')[0];

        if (mode === "carrier") {
          const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          const { data: trucks } = await supabase
            .from('truck_postings')
            .select('id, available_to, status')
            .eq('created_by', user.id)
            .in('status', ['active', 'available']);

          const userTrucks = trucks || [];
          const expiring = userTrucks.filter(t => t.available_to >= today && t.available_to <= tomorrow).length;

          setStats(s => ({
            ...s,
            postedTrucks: userTrucks.length,
            expiringTrucks: expiring,
            isLoading: false,
          }));
          return;
        }

        const { data: loads } = await supabase
          .from('loads')
          .select('id, rate_amount, total_miles, origin_state, destination_state, created_at, equipment_type, pickup_date, offers(id)')
          .eq('created_by', user.id)
          .eq('status', 'posted')
          .gte('pickup_date', today);

        const userLoads = loads || [];

        const loadsWithRpm = userLoads.filter(l => l.total_miles && l.total_miles > 0 && l.rate_amount);
        const avgRpm = loadsWithRpm.length > 0
          ? loadsWithRpm.reduce((s, l) => s + (l.rate_amount! / l.total_miles!), 0) / loadsWithRpm.length
          : 0;
        const lowPriced = avgRpm > 0
          ? loadsWithRpm.filter(l => (l.rate_amount! / l.total_miles!) < avgRpm * 0.85).length
          : 0;

        const noOfferLoads = userLoads.filter(l => {
          const hours = (now.getTime() - new Date(l.created_at || '').getTime()) / (1000 * 60 * 60);
          return (l.offers || []).length === 0 && hours > 6;
        });

        let carrierMatchCount = 0;
        if (userLoads.length > 0) {
          const { data: trucks } = await supabase
            .from('truck_postings')
            .select('id, equipment_type, origin_location, available_from, available_to')
            .in('status', ['active', 'available'])
            .gte('available_to', today)
            .neq('created_by', user.id);

          if (trucks && trucks.length > 0) {
            const seen = new Set<string>();
            for (const load of userLoads) {
              for (const truck of trucks) {
                if (seen.has(truck.id)) continue;
                const truckEq = (truck.equipment_type || "").toLowerCase().replace(/[_\s-]/g, "");
                const loadEq = (load.equipment_type || "").toLowerCase().replace(/[_\s-]/g, "");
                if (truckEq !== loadEq) continue;
                if (load.pickup_date && (load.pickup_date < truck.available_from || load.pickup_date > truck.available_to)) continue;
                const stateMatch = (truck.origin_location || "").match(/,\s*([A-Z]{2})\b/) || (truck.origin_location || "").match(/\b([A-Z]{2})\b/);
                const truckState = stateMatch ? stateMatch[1] : null;
                if (truckState && load.origin_state && truckState !== load.origin_state) continue;
                seen.add(truck.id);
                carrierMatchCount++;
              }
            }
          }
        }

        const laneMap = new Map<string, number>();
        for (const l of userLoads) {
          const key = `${l.origin_state}-${l.destination_state}`;
          laneMap.set(key, (laneMap.get(key) || 0) + 1);
        }

        setStats({
          lowPricedLoads: lowPriced,
          matchingCarriers: carrierMatchCount,
          riskAlerts: noOfferLoads.length,
          underperformingLanes: noOfferLoads.length > 0 ? Math.min(noOfferLoads.length, 5) : 0,
          trendingLanes: laneMap.size,
          expiringTrucks: 0,
          postedTrucks: 0,
          isLoading: false,
        });
      } catch (err) {
        console.error('AIQuickActions error:', err);
        setStats(s => ({ ...s, isLoading: false }));
      }
    };
    fetchStats();
  }, [user, mode]);

  // Collapse inline expansion when switching to desktop
  useEffect(() => {
    if (!isMobile) setExpandedView(null);
  }, [isMobile]);

  // Broker: on mobile, intercept and expand inline; on desktop, swap center pane
  const handleBrokerAction = (view: DashboardViewMode, fallbackHref: string) => {
    if (isMobile) {
      setExpandedView(prev => (prev === view ? null : view));
      return;
    }
    if (onViewChange) onViewChange(view);
    else navigate(fallbackHref);
  };

  if (mode === "carrier") {
    const notWired = () => toast("This action will be wired next.", { description: "Carrier AI logic is rolling out one card at a time." });
    return (
      <div className="flex flex-col gap-2 h-full min-w-0 overflow-hidden">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <AIActionCard
            icon={<DollarSign className="h-3.5 w-3.5 text-accent" />}
            iconBg="bg-info-subtle"
            title="AI Lane Volume"
            description="Track lanes you care about. Save searches and we'll watch volume + rate moves for you."
            actionLabel="Generate insights?"
            onAction={onLaneVolume ?? notWired}
            isLoading={stats.isLoading}
          />
          <AIActionCard
            icon={<Users className="h-3.5 w-3.5 text-success" />}
            iconBg="bg-success-subtle"
            title="AI Load Matching"
            description={stats.postedTrucks > 0
              ? `You have ${stats.postedTrucks} truck${stats.postedTrucks !== 1 ? 's' : ''} posted. Surface live load matches on your lanes.`
              : "Post trucks to surface live load matches on your lanes."}
            actionLabel="Review matches"
            onAction={onLoadMatching ?? notWired}
            isLoading={stats.isLoading}
          />
          <AIActionCard
            icon={<AlertTriangle className="h-3.5 w-3.5 text-warning" />}
            iconBg="bg-warning-subtle"
            title="Truck Expiration Alerts"
            description={stats.expiringTrucks > 0
              ? `${stats.expiringTrucks} of your truck${stats.expiringTrucks !== 1 ? 's' : ''} expire within 24h. Re-post before they fall off the board.`
              : "No trucks expiring soon. We'll flag any that drop off in the next 24h."}
            actionLabel="View Risks"
            onAction={onTruckExpiration ?? notWired}
            isLoading={stats.isLoading}
          />
          <AIActionCard
            icon={<Zap className="h-3.5 w-3.5 text-ai" />}
            iconBg="bg-ai-subtle"
            title="ATLAS Pattern Detector"
            description="ATLAS detects the lanes you run most and suggests trucks to re-post for repeat freight."
            actionLabel="View patterns"
            onAction={onPatternDetector ?? notWired}
            isLoading={stats.isLoading}
          />
          <AIActionCard
            icon={<TrendingUp className="h-3.5 w-3.5 text-brand-teal-deep" />}
            iconBg="bg-brand-teal-subtle"
            title="Market Intelligence"
            description="Spot rates on TX→ATL up 8% this week. 6 lanes paying above your average."
            actionLabel="View Market Report"
            onAction={onMarketIntelligence ?? (() => setMarketOpen(true))}
            isLoading={stats.isLoading}
          />
        </div>
        {!onMarketIntelligence && <MarketIntelligenceDialog open={marketOpen} onOpenChange={setMarketOpen} />}
      </div>
    );
  }

  const brokerCards: Array<{ view: DashboardViewMode; fallback: string; node: React.ReactNode }> = [
    {
      view: "lane-volume",
      fallback: "/atlas-hub",
      node: (
        <AIActionCard
          icon={<DollarSign className="h-3.5 w-3.5 text-accent" />}
          iconBg="bg-info-subtle"
          title="AI Lane Volume"
          description="AI analysis of overall load and truck activity across your lanes."
          actionLabel="Generate insights?"
          onAction={() => handleBrokerAction("lane-volume", "/atlas-hub")}
          isLoading={stats.isLoading}
          isActive={isMobile && expandedView === "lane-volume"}
        />
      ),
    },
    {
      view: "carrier-matches",
      fallback: "/search-trucks",
      node: (
        <AIActionCard
          icon={<Users className="h-3.5 w-3.5 text-success" />}
          iconBg="bg-success-subtle"
          title="AI Carrier Matching"
          description={stats.matchingCarriers > 0 ? `${stats.matchingCarriers} carrier${stats.matchingCarriers !== 1 ? 's' : ''} match your active loads. Send invites?` : "No carrier matches found for your loads yet."}
          actionLabel="Invite Carriers"
          onAction={() => handleBrokerAction("carrier-matches", "/search-trucks")}
          isLoading={stats.isLoading}
          isActive={isMobile && expandedView === "carrier-matches"}
        />
      ),
    },
    {
      view: "risk-alerts",
      fallback: "/my-loads",
      node: (
        <AIActionCard
          icon={<AlertTriangle className="h-3.5 w-3.5 text-warning" />}
          iconBg="bg-warning-subtle"
          title="AI Risk Alerts"
          description="2 carriers in your network had recent insurance warnings."
          actionLabel="View Risks"
          onAction={() => handleBrokerAction("risk-alerts", "/my-loads")}
          isLoading={stats.isLoading}
          isActive={isMobile && expandedView === "risk-alerts"}
        />
      ),
    },
    {
      view: "post-duplicates",
      fallback: "/atlas-hub",
      node: (
        <AIActionCard
          icon={<Zap className="h-3.5 w-3.5 text-ai" />}
          iconBg="bg-ai-subtle"
          title="ATLAS Pattern Detector"
          description="ATLAS detects your posting patterns and suggests lanes to repost."
          actionLabel="View patterns"
          onAction={() => handleBrokerAction("post-duplicates", "/atlas-hub")}
          isLoading={stats.isLoading}
          isActive={isMobile && expandedView === "post-duplicates"}
        />
      ),
    },
    {
      view: "market-intelligence",
      fallback: "/search",
      node: (
        <AIActionCard
          icon={<TrendingUp className="h-3.5 w-3.5 text-brand-teal-deep" />}
          iconBg="bg-brand-teal-subtle"
          title="Market Intelligence"
          description="Spot rates on TX→ATL up 8% this week. 6 lanes trending above your posted rates."
          actionLabel="View Market Report"
          onAction={() => handleBrokerAction("market-intelligence", "/search")}
          isLoading={stats.isLoading}
          isActive={isMobile && expandedView === "market-intelligence"}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-2 h-full min-w-0 overflow-hidden">
      <div className="flex flex-col gap-2 flex-1 min-w-0">
        {brokerCards.map((c) => (
          <Fragment key={c.view}>
            {c.node}
            {isMobile && expandedView === c.view && (
              <InlineExpansion view={c.view} onClose={() => setExpandedView(null)} />
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
